/**
 * GateGate — wraps the whole app behind a shared-password screen.
 *
 * On mount, queries ``GET /api/gate`` to learn whether this client has the
 * gate cookie set (or whether the server has the gate disabled — e.g. in
 * dev). If locked, renders an inline password form instead of the children
 * (the router never mounts). Successfully unlocking via ``POST /api/gate``
 * flips local state to ``unlocked`` and re-renders the children, at which
 * point Firebase auth picks up.
 *
 * This is intentionally NOT a router route: the gate is the outer ring of
 * access, so it should run before any router/auth logic.
 */
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import type { FormEvent, ReactElement, ReactNode } from "react";

const API_BASE: string = ((): string => {
  const meta = import.meta as { env?: { VITE_API_BASE_URL?: string } };
  return meta.env?.VITE_API_BASE_URL ?? "/api";
})();

type GateState = "checking" | "locked" | "unlocked" | "error";

export function GateGate({ children }: { children: ReactNode }): ReactElement {
  const [state, setState] = useState<GateState>("checking");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/gate`, { credentials: "same-origin" });
        if (cancelled) return;
        if (res.ok) {
          const json = (await res.json()) as { unlocked?: boolean };
          setState(json.unlocked ? "unlocked" : "locked");
          return;
        }
        // 404 = server has no gate route; treat as unlocked (dev / staging
        // builds that pre-date the gate).
        setState(res.status === 404 ? "unlocked" : "locked");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking") {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
        role="status"
        aria-label="Checking access"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (state === "error") {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          Could not reach the server to check access. Refresh to try again.
        </Alert>
      </Container>
    );
  }

  if (state === "locked") {
    return <GatePage onUnlock={() => setState("unlocked")} />;
  }

  return <>{children}</>;
}

function GatePage({ onUnlock }: { onUnlock: () => void }): ReactElement {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await fetch(`${API_BASE}/gate`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password }),
        credentials: "same-origin",
      });
      if (res.status === 204 || res.ok) {
        onUnlock();
        return;
      }
      if (res.status === 401) {
        setErrorMessage("Incorrect password.");
      } else {
        setErrorMessage(`Unexpected error (${res.status}).`);
      }
    } catch {
      setErrorMessage("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 12 }}>
      <Stack spacing={3} component="section" aria-label="Access gate" sx={{ alignItems: "center" }}>
        <Avatar sx={{ bgcolor: "primary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Records Monitor
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          This is a limited preview. Enter the shared access password to continue.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Access password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoComplete="current-password"
              disabled={submitting}
              slotProps={{ htmlInput: { "aria-label": "Access password" } }}
            />
            {errorMessage ? (
              <Alert severity="error" role="alert">
                {errorMessage}
              </Alert>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !password}
              fullWidth
            >
              {submitting ? "Unlocking…" : "Continue"}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
